import { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, RefreshControl, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useFocusEffect, useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { Mutation } from '@/utils/react-query/Mutations';
import SearchBox from '@/components/General/inputs/SearchBox';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import CutCard from '@/components/General/CutCard';
import Chip from '@/components/General/Chip';
import { showAlert } from '@/utils/showAlert';
import { createProfileView } from '@/data/profileViews';

/**
 * Org-wide profile views — named JSON configs that pick which fields a
 * ProfileViewCard renders. Requires 'Manage Profile Views'.
 */

// Summary labels derived from a view's config — mirrors the section keys the
// get_profile_view_card RPC can return.
const configSummary = (config = {}) => {
    const labels = [];
    if (Object.values(config.basic || {}).some(Boolean)) labels.push('Basic Info');
    if (Object.values(config.contact || {}).some(Boolean)) labels.push('Contact');
    if ((config.customFieldIDs || []).length > 0) labels.push('Custom Fields');
    if (config.attendance) labels.push('Attendance');
    if (config.tasks) labels.push('Tasks');
    if ((config.roleIDs || []).length > 0) labels.push('Roles');
    if ((config.trainingModuleIDs || []).length > 0) labels.push('Training');
    if (config.smallGroups) labels.push('Small Groups');
    if (config.family) labels.push('Family');
    return labels;
};

const ViewRow = ({ view, onEdit, onDuplicate, onDelete }) => {
    const summary = configSummary(view.config);

    const menuOptions = useMemo(() => ([
        {
            name: 'Edit View',
            icon: dropdownIcons.edit,
            onSelect: () => onEdit(view),
        },
        {
            name: 'Duplicate',
            icon: dropdownIcons.copy,
            onSelect: () => onDuplicate(view),
        },
        {
            name: 'Delete View',
            icon: dropdownIcons.delete,
            destructive: true,
            onSelect: () => showAlert({
                title: 'Delete Profile View?',
                subtitle: `Are you sure you want to delete "${view.name}"? This cannot be undone.`,
                isDestructive: true,
                confirmText: 'Delete',
                onConfirm: () => onDelete(view),
            }),
        },
    ]), [view, onEdit, onDuplicate, onDelete]);

    return (
        <View style={styles.rowWrap} testID={`ProfileViewRow-${view.name}`}>
            <CutCard
                testID={`ProfileViewRow-press-${view.name}`}
                accentColor="people"
                onPress={() => onEdit(view)}
                style={styles.rowCard}
                contentStyle={styles.rowContentWrap}
            >
                <View style={styles.rowIcon}>
                    <Unicon name="id-card-outline" size={18} colorVariant="people" />
                </View>
                <View style={styles.rowContent}>
                    <Text numberOfLines={1} style={styles.rowTitle}>{view.name}</Text>
                    <View style={styles.summaryRow}>
                        {summary.length === 0 ? (
                            <Text style={styles.summaryText}>Name & avatar only</Text>
                        ) : (
                            summary.slice(0, 5).map(label => (
                                <Chip key={label} colorVariant="people">{label}</Chip>
                            ))
                        )}
                        {summary.length > 5 && (
                            <Text style={styles.summaryText}>+{summary.length - 5} more</Text>
                        )}
                    </View>
                </View>
            </CutCard>
            <View style={styles.menuWrap}>
                <DropdownMenu
                    testID="ProfileViewRowMenu"
                    options={menuOptions}
                    triggerComponent={
                        <Pressable accessibilityRole="button" accessibilityLabel="Profile view options" style={styles.menuButton}>
                            <Unicon name="ellipsis-vertical" size={16} colorVariant="textSecondary" />
                        </Pressable>
                    }
                />
            </View>
        </View>
    );
};

export default function ProfileViewsScreen() {
    const router = useRouter();
    const queryClient = useQueryClient();
    const [searchText, setSearchText] = useState('');

    const { data: loggedInUser, isLoading: userLoading } = Query.people.loggedInUser();
    const canManage = loggedInUser?.can?.('Manage Profile Views');

    const { data: views, isLoading } = Query.profileViews.all({ options: { enabled: !!canManage } });
    const deleteMutation = Mutation.profileViews.delete();

    const refresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.profileViews.all() });
    }, [queryClient]);

    useFocusEffect(
        useCallback(() => {
            refresh();
        }, [refresh])
    );

    const filteredViews = useMemo(() => {
        const all = views || [];
        const q = searchText.trim().toLowerCase();
        if (!q) return all;
        return all.filter(v => v.name?.toLowerCase().includes(q));
    }, [views, searchText]);

    const onEdit = useCallback((view) => {
        router.push(`/settings/organization/profile-views/${view.id}`);
    }, [router]);

    const onDuplicate = useCallback(async (view) => {
        const created = await createProfileView({
            name: `${view.name} (Copy)`,
            config: view.config || {},
            createdByPersonID: loggedInUser?.id,
        });
        if (created?.id) refresh();
    }, [loggedInUser, refresh]);

    const onDelete = useCallback((view) => {
        deleteMutation.mutate({ id: view.id });
    }, [deleteMutation]);

    if (userLoading) {
        return <View style={styles.center}><ActivityIndicator /></View>;
    }

    if (!canManage) {
        return (
            <View style={styles.center} testID="profile-views-requires-permission">
                <Unicon name="lock-closed-outline" size={40} colorVariant="textSecondary" />
                <Text style={styles.noAccessText}>You don&apos;t have permission to manage profile views.</Text>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <ScrollView
                contentContainerStyle={styles.contentContainer}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={false} onRefresh={refresh} />}
            >
                <View style={styles.headerRow}>
                    <View style={styles.headerText}>
                        <Text style={styles.headerDescription}>
                            Profile views pick which fields appear on a compact person card wherever the view is placed.
                        </Text>
                    </View>
                    <SolidButton
                        testID="create-profile-view-button"
                        text="New View"
                        iconName="add"
                        iconBefore
                        size="md"
                        onPress={() => router.push('/settings/organization/profile-views/create')}
                    />
                </View>

                <View style={styles.searchContainer}>
                    <SearchBox
                        testID="ProfileViews"
                        searchText={searchText}
                        onChangeText={setSearchText}
                        placeholder="Search profile views..."
                    />
                </View>

                {isLoading && (views || []).length === 0 && (
                    <View style={styles.loadingContainer}>
                        <ActivityIndicator />
                    </View>
                )}

                {!isLoading && filteredViews.length === 0 && (
                    <EmptyListPlaceholder
                        icon="id-card-outline"
                        spacing={60}
                        message={searchText
                            ? 'No profile views match your search.'
                            : 'No profile views yet. Create one to choose which fields a profile card shows.'}
                    />
                )}

                <View style={styles.list}>
                    {filteredViews.map(view => (
                        <ViewRow
                            key={view.id}
                            view={view}
                            onEdit={onEdit}
                            onDuplicate={onDuplicate}
                            onDelete={onDelete}
                        />
                    ))}
                </View>
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, shadows, spacing, typography }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    contentContainer: {
        padding: spacing.md + 4,
        gap: spacing.md,
        _web: {
            maxWidth: 900,
            alignSelf: 'center',
            width: '100%',
        },
    },
    center: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
        gap: spacing.md,
        padding: spacing.lg,
    },
    noAccessText: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacing.md,
    },
    headerText: {
        flex: 1,
    },
    headerDescription: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        lineHeight: 20,
    },
    searchContainer: {
        marginTop: -spacing.xs,
    },
    loadingContainer: {
        paddingVertical: spacing.xl,
        alignItems: 'center',
    },
    list: {
        gap: spacing.sm + 2,
    },
    rowWrap: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
    },
    rowCard: {
        flex: 1,
        minWidth: 0,
        boxShadow: shadows.card,
    },
    rowContentWrap: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingLeft: spacing.sm + 2,
        paddingRight: spacing.sm,
        paddingVertical: spacing.md,
        gap: spacing.sm + 2,
    },
    menuWrap: {
        flexShrink: 0,
    },
    menuButton: {
        padding: spacing.sm,
    },
    rowIcon: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: colors.peopleContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    rowContent: {
        flex: 1,
        minWidth: 0,
        gap: spacing.xs,
    },
    rowTitle: {
        fontSize: typography.body,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    summaryRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: spacing.xs + 1,
    },
    summaryText: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textTertiary,
    },
}));
